import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addGroupMember, getGroupMembers, getGroups, removeGroupMember, updateGroup } from '../api/groupsApi'
import { getUserGroups } from '../api/usersApi'
import type { GroupUpdate } from '../types'

const KEY = 'admin-groups'
const MEMBERS_KEY = 'admin-group-members'
const USER_GROUPS_KEY = 'admin-user-groups'
/** Kullanıcı Yönetimi listesi (`useUsers`) — satırlardaki "Gruplar" kolonu üyelikle değişir. */
const USERS_KEY = 'admin-users'

/**
 * Grup Yönetimi sunucu-durumu (122): grup listesi + ad güncelleme + üye ekleme/çıkarma. Üyelik değişince
 * grup listesi de tazelenir (üye sayısı sütunu) ve Kullanıcı Yönetimi'nin önbelleği düşürülür.
 */
export function useGroups() {
  const qc = useQueryClient()
  const list = useQuery({ queryKey: [KEY], queryFn: getGroups })

  // Üyelik iki ekrandan da değişir (Grup Yönetimi · Kullanıcı Yönetimi); ikisinin görünümü birlikte tazelenir.
  const afterMembership = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
    qc.invalidateQueries({ queryKey: [MEMBERS_KEY] })
    qc.invalidateQueries({ queryKey: [USER_GROUPS_KEY] })
    qc.invalidateQueries({ queryKey: [USERS_KEY] })
  }

  // meta.handledCodes: ad çakışması formda alana yazılır → merkezi hata toast'ı (019) susar.
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: GroupUpdate }) => updateGroup(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })
  const addMember = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: number; userId: number }) => addGroupMember(groupId, userId),
    onSuccess: afterMembership,
  })
  const removeMember = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: number; userId: number }) =>
      removeGroupMember(groupId, userId),
    onSuccess: afterMembership,
  })

  return { list, update, addMember, removeMember }
}

/** Bir grubun üyeleri — satır açıldığında yüklenir. */
export function useGroupMembers(groupId: number) {
  return useQuery({ queryKey: [MEMBERS_KEY, groupId], queryFn: () => getGroupMembers(groupId) })
}

/** Bir kullanıcının dahil olduğu grupların kimlikleri — Kullanıcı Yönetimi'nde satır açıldığında yüklenir. */
export function useUserGroups(userId: number) {
  return useQuery({ queryKey: [USER_GROUPS_KEY, userId], queryFn: () => getUserGroups(userId) })
}
